// app/javascript/shared/theme.ts — Skillth MUI theme v2.3
// 値はすべて tokens.ts（tokens.json から自動生成）を参照する。このファイルに色・数値を直書きしない。
// 目的: 新しい画面が「MUI の標準部品を置くだけ」でルールどおりになるようにする。
import { createTheme } from '@mui/material/styles';
import tokens from './tokens';
const { utility: U, brand: Br, value: V, opacity: Op } = tokens;
const F = V.sizing.font;
const R = V.sizing.radius;
const S = V.spacing;
const C = V.sizing.component;
const line = (w, c) => `${w}px solid ${c}`;
const BORDER = V.sizing.border;
const heading = { lineHeight: V.sizing.lineHeight.heading, fontWeight: tokens.common.string.fontWeight.bold };
const body = { lineHeight: V.sizing.lineHeight.body };
const theme = createTheme({
    palette: {
        primary: { main: Br.navy.primary, dark: Br.navy.deep, contrastText: U.function.onColor },
        secondary: { main: Br.navy.secondary, light: Br.navy.tertiarySoft, contrastText: U.function.onColor },
        background: { default: U.neutral.secondary, paper: U.neutral.background },
        text: {
            primary: U.neutral.text,
            secondary: U.neutral.annotation, // MUI 部品の補足文字。読める色（#626A70）
            subtle: U.neutral.annotation,
            disabled: U.neutral.disabled, // #8D98A0 は無効・プレースホルダーだけ
        },
        divider: U.line.default,
        warning: { main: U.function.warning, dark: U.function.caution, light: U.base.orange, contrastText: U.neutral.text },
        error: { main: U.function.required, dark: Br.red.deep, light: U.base.red, contrastText: U.function.onColor },
        success: { main: U.function.success, light: U.base.green, contrastText: U.function.onColor },
        info: { main: U.function.info, contrastText: U.function.onColor },
        action: { hover: U.base.hover, selected: U.base.selected },
        neutral: U.neutral,
        line: U.line,
    },
    // PC のみ対応のため responsiveFontSizes は使わない
    typography: {
        fontFamily: `'${tokens.common.string.fontName.japan}', ${tokens.common.string.fontName.latin}, -apple-system, sans-serif`,
        fontWeightRegular: tokens.common.string.fontWeight.regular,
        fontWeightMedium: tokens.common.string.fontWeight.medium,
        fontWeightBold: tokens.common.string.fontWeight.bold,
        h1: Object.assign({ fontSize: F.decoration.h1 }, heading),
        h2: Object.assign({ fontSize: F.decoration.h2 }, heading),
        h3: Object.assign({ fontSize: F.decoration.h3 }, heading),
        h4: Object.assign({ fontSize: F.normal.h4 }, heading),
        h5: Object.assign({ fontSize: F.normal.h5 }, heading),
        h6: Object.assign({ fontSize: F.normal.h6 }, heading),
        body1: Object.assign({ fontSize: F.normal.p1 }, body),
        body2: Object.assign({ fontSize: F.normal.p2 }, body),
        caption: Object.assign({ fontSize: F.normal.p3 }, body),
        button: { fontSize: F.console.row, fontWeight: tokens.common.string.fontWeight.medium, textTransform: 'none' },
        consoleTitle: Object.assign({ fontSize: F.console.h1 }, heading),
        consoleHeading: Object.assign({ fontSize: F.console.h3 }, heading),
        row: Object.assign({ fontSize: F.console.row }, body),
        meta: { fontSize: F.console.meta, lineHeight: V.sizing.lineHeight.heading, fontWeight: tokens.common.string.fontWeight.medium },
    },
    spacing: S.set.small, // 8px グリッド（sx の p: 2 = 16px = Spacing/Card/Medium）
    shape: { borderRadius: R.card },
    components: {
        MuiTypography: { defaultProps: { variantMapping: { consoleTitle: 'h1', consoleHeading: 'h2', row: 'span', meta: 'span' } } },
        MuiCssBaseline: {
            styleOverrides: {
                body: { fontVariantNumeric: 'tabular-nums' },
                a: { color: U.function.link },
                'a:hover': { color: U.function.linkHover },
            },
        },
        MuiButton: {
            defaultProps: { disableElevation: true },
            styleOverrides: {
                root: { minHeight: C.control, borderRadius: R.button, padding: `0 ${S.card.medium}px`, textTransform: 'none', fontSize: F.console.row, fontWeight: tokens.common.string.fontWeight.medium },
                outlined: { borderColor: U.line.control, color: U.neutral.text },
            },
        },
        MuiIconButton: { styleOverrides: { root: { width: C.control, height: C.control } } }, // aria-label 必須
        MuiOutlinedInput: {
            styleOverrides: { root: { minHeight: C.control, borderRadius: R.input }, notchedOutline: { borderColor: U.line.control } },
        },
        MuiInputLabel: { styleOverrides: { root: { color: U.neutral.annotation } } },
        MuiChip: {
            styleOverrides: {
                root: { height: C.chip, borderRadius: R.tag, fontSize: F.console.meta, fontWeight: tokens.common.string.fontWeight.medium },
                outlined: { borderColor: U.line.control, color: U.neutral.text },
                colorSuccess: { backgroundColor: U.base.green, color: U.function.success },
                colorError: { backgroundColor: U.base.red, color: U.function.required },
                colorWarning: { backgroundColor: U.base.orange, color: U.function.caution },
            },
        },
        MuiPaper: { defaultProps: { elevation: 0 }, styleOverrides: { outlined: { borderColor: U.line.default } } },
        MuiCard: { defaultProps: { variant: 'outlined' }, styleOverrides: { root: { borderColor: U.line.default, borderRadius: R.card } } },
        MuiCardContent: { styleOverrides: { root: { padding: S.card.medium, '&:last-child': { paddingBottom: S.card.medium } } } },
        MuiTableCell: {
            styleOverrides: {
                root: { fontSize: F.console.row, paddingTop: S.set.medium, paddingBottom: S.set.medium, borderBottom: line(BORDER.default, U.line.faint) },
                head: { fontSize: F.normal.p3, fontWeight: tokens.common.string.fontWeight.medium, color: U.neutral.annotation, backgroundColor: U.neutral.tableHead, borderBottom: line(BORDER.default, U.line.subtle) },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    '&.MuiTableRow-hover:hover': { backgroundColor: U.base.hover },
                    '&.Mui-selected': { backgroundColor: U.base.selected, boxShadow: `inset ${BORDER.tertiary}px 0 0 ${U.function.select}` },
                    '&.Mui-selected:hover': { backgroundColor: Op.navy.v12 },
                },
            },
        },
        MuiDivider: { styleOverrides: { root: { borderColor: U.line.subtle } } },
        MuiLink: { defaultProps: { underline: 'always' }, styleOverrides: { root: { color: U.function.link, '&:hover': { color: U.function.linkHover } } } },
        MuiTab: { styleOverrides: { root: { minHeight: C.control, textTransform: 'none', fontSize: F.normal.p2 } } },
        MuiAlert: { styleOverrides: { root: { borderRadius: R.card, fontSize: F.normal.p2 } } },
        MuiSnackbar: { defaultProps: { anchorOrigin: { vertical: 'bottom', horizontal: 'right' } } },
        MuiTooltip: { defaultProps: { arrow: true } },
    },
});
export default theme;
export { theme };
